import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import GlobalNav from './GlobalNav';
import ContractSigningModal from './ContractSigningModal';
import { roleHome } from '../utils/roleHome';
import { saveIntendedRoute } from '../utils/postAuthRedirect';

/**
 * Iter-178 P0 — preserve the originally-requested URL when bouncing the
 * user to /login. Login.js reads ?redirect=… (query string) OR
 * location.state.from to land them back on the exact gig/page after
 * authentication. This is what makes deep-links from the gig alert
 * email ("View & Apply") arrive at the right shift, not the dashboard.
 *
 * Iter-313k — Verification gating. Talent must complete IDV, and
 * clients/agencies must complete Business Verification, before they can
 * access any portal page.
 */

// Paths a talent can visit BEFORE their IDV is approved. Everything
// else redirects them to /talent/idv.
const TALENT_IDV_ALLOWED_PATHS = [
  '/talent/idv',
  '/talent/verify',
  '/talent/id-verification',
  '/talent/notifications',        // account notifications (approval, etc.)
  '/help',                        // support
  '/settings/security',           // password change
];

// Paths a pending client/agency can visit BEFORE approval. Everything
// else redirects them to /client/pending.
const CLIENT_PENDING_ALLOWED_PATHS = [
  '/client/pending',
  '/client/business-verification',
];

const _pathAllowed = (path, allowlist) => {
  if (!path) return false;
  return allowlist.some((p) => path === p || path.startsWith(p + '/'));
};

// iter431 — Paths a talent may visit while the HARD ordered onboarding gate is
// active and they are not yet complete. Everything else bounces to their
// current step. Covers every step's screen plus onboarding utilities.
const TALENT_ONBOARDING_ALLOWED_PATHS = [
  '/talent/onboarding',           // the progress hub
  '/verify-phone',                // step 1
  '/talent/profile-info',         // step 2
  '/talent/profile',              // step 2 (alias)
  '/talent/profile-complete',     // step 2 (alias)
  '/talent/idv',                  // steps 3-4
  '/talent/verify',
  '/talent/id-verification',
  '/talent/agreement',            // step 5
  '/talent/notifications',
  '/help',
  '/settings/security',
];

export const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, onboarding, refreshOnboarding } = useAuth();
  const location = useLocation();

  // iter431 — while the ordered gate is active for an incomplete talent, keep
  // the onboarding state fresh on every navigation so finishing a step (profile
  // save, IDV upload, agreement sign) immediately advances the gate instead of
  // bouncing back to the just-completed step.
  React.useEffect(() => {
    if (
      user?.role === 'talent' &&
      onboarding?.gate_enabled === true &&
      onboarding?.complete === false &&
      typeof refreshOnboarding === 'function'
    ) {
      refreshOnboarding();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    const dest = `${location.pathname}${location.search || ''}`;
    // Persist the intended destination so it survives EVERY auth path —
    // social OAuth round-trips and sign-up both lose the ?redirect= query
    // param, but can restore the target from sessionStorage.
    saveIntendedRoute(dest);
    return (
      <Navigate
        to={`/login?redirect=${encodeURIComponent(dest)}`}
        state={{ from: dest }}
        replace
      />
    );
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Iter-296 P0 — Admins (and super-admins) always retain universal
    // access. The Admin Messages Center's "Open related page" deep-links
    // into /talent/* and /client/* routes for inspection / impersonation;
    // bouncing those to "/" forced a re-render that landed admins on
    // /login (since "/" → Navigate("/login") for unauthenticated users
    // and the auth check raced ahead of the user state hydration).
    const isAdmin = user.role === 'admin' || user.role === 'super_admin';
    if (!isAdmin) {
      // Iter-417 — send the role-mismatched user to THEIR home, never to
      // "/" (which redirects to /login and looks like a forced logout).
      return <Navigate to={roleHome(user.role)} replace />;
    }
  }

  // Iter-313k — Talent IDV gating. If the user is a talent whose IDV
  // is not yet approved, they can only visit the IDV upload page (and
  // a small set of onboarding utility pages). Impersonating admins are
  // exempt so ops can inspect any account.
  const isImpersonating = (
    typeof localStorage !== 'undefined' &&
    localStorage.getItem('impersonating') === 'true'
  );

  // iter433 — On-Hold-for-Payment gate (CLIENT / AGENCY only). A held account
  // can ONLY reach the restriction screen + its payment / Evie / support
  // escape hatches; the backend PaymentHoldGate enforces the same server-side
  // so an active session cannot bypass it. Impersonating admins are exempt.
  // Using `=== true` avoids a false bounce while /auth/me hydrates.
  const _phPath = location.pathname;
  const _phAllowed = (
    _phPath === '/account/payment-hold' ||
    _phPath.startsWith('/client/messages/evie') ||
    _phPath.startsWith('/client/messages/support') ||
    _phPath.startsWith('/client/billing') ||
    _phPath.startsWith('/client/invoices') ||
    _phPath.startsWith('/pay')
  );
  if (
    (user.role === 'client' || user.role === 'agency') &&
    user.payment_hold === true &&
    !isImpersonating &&
    !_phAllowed
  ) {
    return <Navigate to="/account/payment-hold" replace />;
  }

  // iter430 — SMS phone verification applies to TALENT ONLY. Talent must
  // prove a working mobile number (one-time SMS code) before any portal page
  // unlocks. Clients and agencies are NOT gated — they log in straight to
  // their schedules/bookings. Using `=== false` (not falsy) avoids a false
  // bounce while /auth/me is still hydrating; the backend PhoneVerificationGate
  // middleware hard-enforces the same talent-only rule regardless of the UI.
  if (
    user.role === 'talent' &&
    user.phone_verified === false &&
    user.phone_verification_required === true &&
    !isImpersonating &&
    location.pathname !== '/verify-phone' &&
    location.pathname !== '/talent/onboarding'
  ) {
    saveIntendedRoute(`${location.pathname}${location.search || ''}`);
    return <Navigate to="/verify-phone" replace />;
  }

  // iter431 — Ordered onboarding HARD gate (talent only). When the master
  // switch is ON (platform_settings.talent_onboarding_gate_enabled), talent
  // who have not finished the sequence (phone → profile → ID → IDV → agreement)
  // may ONLY reach their current step + onboarding utility screens; everything
  // else bounces to their exact next step. The backend TalentOnboardingGate
  // enforces the same server-side. Impersonating admins are exempt.
  // When the switch is OFF, we fall back to the pre-existing IDV gate below so
  // production behaviour is unchanged until the owner authorizes activation.
  const gateOn = onboarding?.gate_enabled === true;
  if (
    user.role === 'talent' &&
    !isImpersonating &&
    gateOn &&
    onboarding &&
    onboarding.complete === false &&
    !_pathAllowed(location.pathname, TALENT_ONBOARDING_ALLOWED_PATHS)
  ) {
    saveIntendedRoute(`${location.pathname}${location.search || ''}`);
    return <Navigate to={onboarding.current_route || '/talent/onboarding'} replace />;
  }

  if (
    user.role === 'talent' &&
    !gateOn &&
    user.idv_status &&
    user.idv_status !== 'approved' &&
    !isImpersonating &&
    !_pathAllowed(location.pathname, TALENT_IDV_ALLOWED_PATHS)
  ) {
    // Preserve the deep link (e.g. a tapped job) through the IDV gate so the
    // talent is returned to it once verification is completed.
    saveIntendedRoute(`${location.pathname}${location.search || ''}`);
    return <Navigate to="/talent/idv" replace />;
  }

  // Iter-209 — Block pending/rejected clients from any client route
  // except the pending screen itself. Existing clients auto-approved.
  // Iter-225q — Agency Workspace accounts are now also gated behind
  // admin approval (no more immediate-access bypass). Pending agencies
  // share the same /client/pending review surface as clients.
  if (
    (user.role === 'client' || user.role === 'agency') &&
    user.client_approval_status &&
    user.client_approval_status !== 'approved' &&
    !_pathAllowed(location.pathname, CLIENT_PENDING_ALLOWED_PATHS)
  ) {
    saveIntendedRoute(`${location.pathname}${location.search || ''}`);
    return <Navigate to="/client/pending" replace />;
  }

  // Approved clients/agencies must sign the required Service Agreement before
  // using the platform. The modal auto-appears (blocking) whenever there is a
  // pending required contract and renders nothing once everything is signed.
  const showClientAgreementGate =
    (user.role === 'client' || user.role === 'agency') &&
    user.client_approval_status === 'approved' &&
    !isImpersonating;

  return (
    <GlobalNav>
      {showClientAgreementGate && (
        <ContractSigningModal mode="modal" onComplete={() => window.location.reload()} />
      )}
      {children}
    </GlobalNav>
  );
};

export default ProtectedRoute;
